import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import api from "../services/api";
import { setShowLoginModal } from "../store/authSlice";
import { store } from "../store";
import { refreshAccessToken } from "../utils/auth";
import { FacebookAuth } from "../utils/facebookAuth";

interface User {
  email: string;
  role: string;
  isSuperAdmin: boolean;
  isSubscribed: boolean;
  enrolledCourses: string[];
  name: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  avatar?: string;
  isActive: boolean;
  courseProgress: {
    courseId: string;
    progress: number;
  }[];
  subscriptionPlan?: string;
  subscriptionStatus?: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string>;
  setUser: (user: User | null) => void;
  signup: (email: string, password: string, username: string) => Promise<void>;
  socialLogin: (provider: 'google' | 'facebook') => Promise<void>;
  checkSubscriptionStatus: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const logout = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  useEffect(() => {
    const accessToken = localStorage.getItem("access_token");
    const userData = localStorage.getItem("user");
    if (accessToken && userData) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
    }
  }, []);

  const getAccessToken = useCallback(async () => {
    try {
      const currentToken = localStorage.getItem("access_token");
      if (!currentToken) throw new Error("No access token");

      // Decode token to check expiration
      const tokenData = JSON.parse(atob(currentToken.split(".")[1]));
      const expirationTime = tokenData.exp * 1000; // Convert to milliseconds

      // If token will expire in next 2 minutes, refresh it
      if (expirationTime - Date.now() < 120000) {
        try {
          const newToken = await refreshAccessToken();
          //console.log("newToken", newToken);
          localStorage.setItem("access_token", newToken);
          return newToken;
        } catch (refreshError) {
          // If refresh fails, try using current token if it's still valid
          if (Date.now() < expirationTime) {
            return currentToken;
          }

          //console.log("refreshError", refreshError);
          throw refreshError;
        }
      }

      return currentToken;
    } catch (error) {
      logout();
      console.log("error", error);
      throw error;
    }
  }, [logout]);

  // Set up periodic token check
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkToken = async () => {
      try {
        await getAccessToken();
      } catch (error) {
        console.error("Token refresh failed:", error);
      }
    };

    const interval = setInterval(checkToken, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, getAccessToken]);

  const login = async (email: string, password: string) => {
    try {
      // Direct login request without token refresh
      const { data } = await api.post(
        "/auth/user/login",
        {
          email,
          password,
        },
        {
          // Skip token refresh interceptor for login
          headers: {
            skipAuthRefresh: "true",
          },
        }
      );

      if (data.access_token) {
        // Store tokens and user data
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        localStorage.setItem("user", JSON.stringify(data.user));

        // Update state
        setUser(data.user);
        setIsAuthenticated(true);

        // Close login modal using store.dispatch
        store.dispatch(setShowLoginModal(false));

        // Check subscription status and redirect accordingly
        try {
          const hasActiveSubscription = await checkSubscriptionStatus();
          if (!hasActiveSubscription) {
            window.location.href = '/pricing';
          } else {
            // User has active subscription, redirect to learning dashboard
            window.location.href = '/learning';
          }
        } catch (error) {
          console.error('Error checking subscription after login:', error);
          // Redirect to pricing anyway if check fails
          window.location.href = '/pricing';
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      throw new Error("Login failed. Please check your credentials.");
    }
  };

  const socialLogin = async (provider: 'google' | 'facebook') => {
    try {
      if (provider === 'facebook') {
        // Use Facebook SDK for frontend authentication
        const { accessToken, user } = await FacebookAuth.login();
        
        // Send Facebook data to backend
        const response = await api.post('/auth/facebook-web', {
          accessToken,
          email: user.email,
          name: user.name,
          profilePicture: user.picture?.data?.url || '',
          providerId: user.id,
        });

        if (response.data.access_token) {
          // Store tokens and user data
          localStorage.setItem("access_token", response.data.access_token);
          localStorage.setItem("refresh_token", response.data.refresh_token);
          localStorage.setItem("user", JSON.stringify(response.data.user));

          // Update state
          setUser(response.data.user);
          setIsAuthenticated(true);

          // Modal will be closed by the LoginModal component

          // Check subscription status and redirect accordingly
          try {
            const hasActiveSubscription = await checkSubscriptionStatus();
            if (!hasActiveSubscription) {
              window.location.href = '/pricing';
            } else {
              // User has active subscription, redirect to learning dashboard
              window.location.href = '/learning';
            }
          } catch (error) {
            console.error('Error checking subscription after social login:', error);
            // Redirect to pricing anyway if check fails
            window.location.href = '/pricing';
          }
        }
      } else {
        // Redirect to the backend Google OAuth endpoint
        window.location.href = `${process.env.REACT_APP_API_URL}/auth/${provider}`;
      }
    } catch (error) {
      console.error(`${provider} login error:`, error);
      throw new Error(`${provider} login failed. Please try again.`);
    }
  };

  const signup = async (email: string, password: string, username: string) => {
    try {
      const { data } = await api.post("/auth/user/register", {
        email,
        password,
        username,
      });

      if (data.access_token) {
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setUser(data.user);
        setIsAuthenticated(true);
        
        // Redirect new users to pricing page
        window.location.href = '/pricing';
      }
    } catch (error) {
      console.error("Signup error:", error);
      throw error;
    }
  };

  const checkSubscriptionStatus = async (): Promise<boolean> => {
    try {
      const response = await api.get('/subscriptions/status/my-subscription');
      const subscription = response.data;
      
      // Check if user has an active subscription
      if (subscription && subscription.isActive && !subscription.isExpired) {
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      // If no subscription found or error, assume no active subscription
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
        getAccessToken,
        setUser,
        signup,
        socialLogin,
        checkSubscriptionStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
